import Reveal from "@/components/feature/Reveal";
import PriceGroupHeading from "@/components/feature/PriceGroupHeading";
import {
  ConcernList,
  FeatureList,
  MenuList,
  TreatmentIndexNav,
  TreatmentSection,
} from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable, RiskList } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentHero from "@/pages/treatment/components/TreatmentHero";
import TreatmentPriceLink from "@/pages/treatment/components/TreatmentPriceLink";
import HairRemovalMonitorLink from "@/pages/treatment/hair-removal/components/HairRemovalMonitorLink";
import HairRemovalPriceNav, {
  ladiesPriceNav,
  mensPriceNav,
} from "@/pages/treatment/hair-removal/components/HairRemovalPriceNav";
import { priceTabs } from "@/mocks/priceTables";
import type { TreatmentPageData } from "@/mocks/treatmentPages";
import { isPendingValue } from "@/utils/isPendingContent";

/**
 * 医療脱毛 親ページの正式コンテンツページ。
 * NOTE: 効果断定表現・未確認の医療情報（使用機器名・レーザー種類・波長・照射方式・施術時間・
 *       麻酔方法・推奨回数・推奨間隔・料金など）は掲載しない。未確定の項目は「—」または「確認中」
 *       として、確定後に追記できる構造にしている。
 *       他カテゴリ（お肌管理・再生医療・注入治療・二重目元）と同じ情報階層・トーンに揃える。
 */

/** 料金表ページ（医療脱毛）の部位別定価を共通利用する */
const hairRemovalGroups = priceTabs.find((tab) => tab.id === "hair-removal")?.groups ?? [];

/**
 * 料金グループ見出しのアンカー（男女を明確に分離）。
 * メガメニュー／お悩みメニューの #hair-ladies / #hair-mens 等は、この料金位置へ接続する。
 */
const hairPriceGroupAnchors: Record<string, string> = {
  "女性": "hair-ladies",
  "男性（顔・首）": "hair-mens",
  "男性（ボディ）": "hair-mens-body",
};

/**
 * 特定行へのアンカー（女性：全身・顔・VIO ／ 男性：ヒゲ・VIO）。
 * 同じIDが重複しないよう、代表となる1行にのみ設定する。
 */
const hairPriceRowAnchors: Record<string, string> = {
  "全身（顔・VIOなし）": "hair-ladies-full",
  "顔": "hair-ladies-face",
  "VIO": "hair-ladies-vio",
  "鼻下": "hair-mens-beard",
  "Vライン": "hair-mens-vio",
};

/**
 * 女性料金ブロック（配色は従来どおり明るい背景・濃い文字のまま）。
 * 料金・並び順・アンカーは変更せず、男性側だけを配色反転するためグループを男女で分ける。
 */
const ladiesPriceGroups = hairRemovalGroups.filter((group) => !group.title.startsWith("男性"));

/**
 * 男性料金ブロック（チャコール背景 × ウォームホワイト文字へ反転）。
 * 「男性（顔・首）」＋「男性（ボディ）」をひと続きのダークトーンエリアとして一体化する。
 */
const mensPriceGroups = hairRemovalGroups.filter((group) => group.title.startsWith("男性"));

export default function HairRemovalDetail({ data }: { data: TreatmentPageData }) {
  const priceHref = `/price?tab=${data.priceTab}`;
  // 未確定（「使用機器：確認中」等）は行ごと非表示にする
  const methodItems = (
    data.method ?? [
      { label: "分類", value: "MEDICAL HAIR REMOVAL" },
      { label: "施術方法", value: "医療レーザー脱毛" },
      { label: "使用機器", value: "確認中" },
    ]
  ).filter((item) => !isPendingValue(item.value));

  // 施術時間・麻酔・ダウンタイム等がすべて未確定の場合はセクションごと非表示にする
  const specRows = data.specs.filter((spec) => !isPendingValue(spec.value));

  const indexItems = [
    { label: "お悩み", target: "tr-concerns" },
    { label: "この施術について", target: "tr-about" },
    { label: "特徴", target: "tr-features" },
    { label: "施術情報", target: "tr-method" },
    { label: "対応メニュー", target: "tr-menu" },
    { label: "施術の流れ", target: "tr-flow" },
    ...(specRows.length > 0 ? [{ label: "ダウンタイム", target: "tr-spec" }] : []),
    { label: "リスク・注意事項", target: "tr-risk" },
    { label: "料金", target: "tr-price" },
    { label: "Q&A", target: "tr-faq" },
  ];

  return (
    <>
      <TreatmentHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      {/* 01 CONCERNS */}
      <TreatmentSection id="tr-concerns" en="CONCERNS" jp="このようなお悩みの方へ" tone="tint">
        <ConcernList items={data.concerns} />
      </TreatmentSection>

      {/* 02 ABOUT */}
      <TreatmentSection id="tr-about" en="ABOUT" jp="施術について" tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            {data.about.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 03 FEATURES */}
      <TreatmentSection id="tr-features" en="FEATURES" jp="特徴" tone="tint">
        <FeatureList items={data.features} />
      </TreatmentSection>

      {/* 04 METHOD */}
      <TreatmentSection id="tr-method" en="METHOD" jp="施術情報" tone="plain">
        <Reveal>
          <MethodTable items={methodItems} />
        </Reveal>
      </TreatmentSection>

      {/* 05 MENU */}
      <TreatmentSection id="tr-menu" en="MENU" jp={data.menu.title} lead={data.menu.lead} tone="tint">
        {data.jump ? (
          <div className="mb-12 md:mb-14">
            {data.jump.lead ? (
              <p className="mb-6 max-w-xl text-[13px] leading-[2] text-foreground-700">{data.jump.lead}</p>
            ) : null}
            <div className="flex flex-wrap gap-2.5">
              {data.jump.items.map((item) => (
                <a
                  key={item.target}
                  href={`#${item.target}`}
                  className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-[12.5px] text-foreground-800 transition-colors hover:border-accent-400 hover:bg-secondary-100/70"
                >
                  {item.label}
                  <i
                    className="ri-arrow-down-line text-sm text-foreground-500 transition-colors group-hover:text-accent-700"
                    aria-hidden
                  />
                </a>
              ))}
            </div>
          </div>
        ) : null}
        <MenuList items={data.menu.items} priceHref={priceHref} />
      </TreatmentSection>

      {/* 06 FLOW */}
      <TreatmentSection id="tr-flow" en="FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={data.flow} />
      </TreatmentSection>

      {/* 07 DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {specRows.length > 0 ? (
        <TreatmentSection id="tr-spec" en="TIME / DOWNTIME" jp="施術時間・ダウンタイム等" tone="tint">
          <DowntimeTable items={specRows} />
        </TreatmentSection>
      ) : null}

      {/* 08 RISK */}
      <TreatmentSection id="tr-risk" en="RISK / NOTES" jp="リスク・副作用・注意事項" tone="plain">
        <Reveal>
          <RiskList items={data.risks} note={data.riskNote} />
        </Reveal>
      </TreatmentSection>

      {/* 09 PRICE */}
      <TreatmentSection id="tr-price" en="PRICE" jp="医療脱毛の料金" tone="tint">
        <p className="max-w-3xl text-[12.5px] leading-[2] text-foreground-600">
          女性・男性、部位ごとに1回の定価をご案内します。複数回のコース料金は、対面カウンセリングにてご案内しています。
        </p>
        {/* 女性ブロック：男性ブロックと同じ左右の内部余白（px-6 / md:px-10）を設定し、
            目次・見出し・料金行の開始／終了X座標を男性側と揃える。背景色は付けない（明るい背景のまま）。 */}
        <div className="mt-10 max-w-3xl px-6 md:px-10">
          <HairRemovalPriceNav group={ladiesPriceNav} tone="light" />
          <div className="mt-12">
            {ladiesPriceGroups.map((group) => {
              const groupAnchor = hairPriceGroupAnchors[group.title];
              return (
                <div
                  key={group.title}
                  id={groupAnchor}
                  data-anchor={groupAnchor ? true : undefined}
                  className="mt-10 first:mt-0"
                >
                  <PriceGroupHeading en={group.en} title={group.title} tone="light" />
                  <div className="mt-4 border-t border-foreground-950/20">
                    {group.rows.map((row) => {
                      const rowAnchor = hairPriceRowAnchors[row.name];
                      return (
                        <div
                          key={row.name}
                          id={rowAnchor}
                          data-anchor={rowAnchor ? true : undefined}
                          className="grid grid-cols-[1.6fr_1fr] items-baseline border-b border-foreground-950/12 py-4"
                        >
                          <span className="pr-6 font-heading text-[15px] text-foreground-950">{row.name}</span>
                          <span className="text-right font-label text-[15px] text-foreground-900">{row.price}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 男性：目次 → 料金（男性目次から料金の終了までを一続きのダークエリアに統一） */}
        {mensPriceGroups.length > 0 ? (
          <div className="mt-14 max-w-3xl bg-foreground-950 px-6 py-10 md:px-10 md:py-12">
            <HairRemovalPriceNav group={mensPriceNav} tone="dark" />
            <div className="mt-12">
              {mensPriceGroups.map((group) => {
                const groupAnchor = hairPriceGroupAnchors[group.title];
                return (
                  <div
                    key={group.title}
                    id={groupAnchor}
                    data-anchor={groupAnchor ? true : undefined}
                    className="mt-10 first:mt-0"
                  >
                    {/* グループ見出し：女性側と共通コンポーネント（PriceGroupHeading／tone=dark）。
                        見出しのDOM構造・列幅・gap・font-size・ラインは男女で完全共通、違うのは表示テキストと配色のみ。 */}
                    <PriceGroupHeading en={group.en} title={group.title} tone="dark" />
                    <div className="mt-4 border-t border-background-50/25">
                      {group.rows.map((row) => {
                        const rowAnchor = hairPriceRowAnchors[row.name];
                        return (
                          <div
                            key={row.name}
                            id={rowAnchor}
                            data-anchor={rowAnchor ? true : undefined}
                            className="grid grid-cols-[1.6fr_1fr] items-baseline border-b border-background-50/15 py-4"
                          >
                            <span className="pr-6 font-heading text-[15px] text-background-50">{row.name}</span>
                            <span className="text-right font-label text-[15px] text-background-100">{row.price}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <p className="mt-6 max-w-3xl text-[12px] leading-[2] text-foreground-500">表示価格はすべて税込です。</p>
        <div className="mt-8">
          <TreatmentPriceLink categoryId="hair-removal" categoryJp="医療脱毛" />
        </div>
        {/* 初めての方限定モニター専用LP（公式サイトにモニター価格は転載しない） */}
        <div className="mt-6 md:mt-8">
          <HairRemovalMonitorLink />
        </div>
      </TreatmentSection>

      {/* 10 Q&A */}
      <TreatmentSection id="tr-faq" en="Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={data.faqs} />
        </div>
      </TreatmentSection>

      {/* FINAL CTA */}
      <TreatmentCta lineLabel={data.ctaLabel} treatmentJp={data.jp} />
    </>
  );
}